import React, { useState, useEffect } from 'react';
import { Shield } from 'lucide-react';
import { api } from '../../utils/api';
import { formatTime } from '../../utils/formatters';

const CompliancePanel = ({ drivers }) => {
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [driverState, setDriverState] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadDriverState = async (driverId) => {
    setLoading(true);
    try {
      const state = await api(`/drivers/${driverId}/state`);
      setDriverState(state);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedDriver) {
      loadDriverState(selectedDriver);
    }
  }, [selectedDriver]);

  return (
    <div className="space-y-4 sm:space-y-6">
      <h2 className="text-lg sm:text-xl font-semibold text-white">Compliance Monitor</h2>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Driver List */}
        <div className="bg-slate-800/50 rounded-2xl border border-slate-700 p-3 sm:p-4">
          <h3 className="text-base sm:text-lg font-medium text-white mb-3 sm:mb-4">Select Driver</h3>
          <div className="space-y-2 max-h-64 sm:max-h-80 overflow-y-auto">
            {drivers.length === 0 ? (
              <p className="text-slate-400 text-center py-4 text-sm">No drivers</p>
            ) : (
              drivers.map(driver => (
                <button
                  key={driver.driver_id}
                  onClick={() => setSelectedDriver(driver.driver_id)}
                  className={`w-full p-2.5 sm:p-3 rounded-xl text-left transition ${
                    selectedDriver === driver.driver_id
                      ? 'bg-emerald-500/20 border border-emerald-500/50'
                      : 'bg-slate-700/30 hover:bg-slate-700/50'
                  }`}
                  data-testid={`driver-select-${driver.driver_id}`}
                >
                  <p className="text-white font-medium text-sm sm:text-base">{driver.users?.first_name} {driver.users?.last_name}</p>
                  <p className="text-xs sm:text-sm text-slate-400">{driver.license_number || 'No license'}</p>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Compliance Status */}
        <div className="lg:col-span-2 space-y-3 sm:space-y-4">
          {!selectedDriver ? (
            <div className="bg-slate-800/50 rounded-2xl border border-slate-700 p-6 sm:p-8 text-center">
              <Shield className="w-10 h-10 sm:w-12 sm:h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400 text-sm sm:text-base">Select a driver to view compliance status</p>
            </div>
          ) : loading ? (
            <div className="bg-slate-800/50 rounded-2xl border border-slate-700 p-6 sm:p-8 text-center">
              <p className="text-slate-400 text-sm sm:text-base">Loading...</p>
            </div>
          ) : driverState ? (
            <>
              {/* Status Cards */}
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div className="bg-slate-800/50 rounded-2xl border border-slate-700 p-4 sm:p-6">
                  <p className="text-slate-400 text-xs sm:text-sm">Continuous Work</p>
                  <p className="text-xl sm:text-2xl font-bold text-white mt-1">{formatTime(driverState.W_cont_seconds)}</p>
                  <div className="mt-2 h-1.5 sm:h-2 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-emerald-500 rounded-full"
                      style={{ width: `${Math.min((driverState.W_cont_seconds / 18900) * 100, 100)}%` }}
                    />
                  </div>
                  <p className="text-xs text-slate-400 mt-1">Max: 5:15</p>
                </div>

                <div className="bg-slate-800/50 rounded-2xl border border-slate-700 p-4 sm:p-6">
                  <p className="text-slate-400 text-xs sm:text-sm">24h Work</p>
                  <p className="text-xl sm:text-2xl font-bold text-white mt-1">{formatTime(driverState.W_24_seconds)}</p>
                  <div className="mt-2 h-1.5 sm:h-2 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 rounded-full"
                      style={{ width: `${Math.min((driverState.W_24_seconds / 43200) * 100, 100)}%` }}
                    />
                  </div>
                  <p className="text-xs text-slate-400 mt-1">Max: 12:00</p>
                </div>

                <div className="bg-slate-800/50 rounded-2xl border border-slate-700 p-4 sm:p-6">
                  <p className="text-slate-400 text-xs sm:text-sm">Rest Credits</p>
                  <p className="text-xl sm:text-2xl font-bold text-white mt-1">{driverState.R_credits_24} / 4</p>
                  <div className="flex gap-1 mt-2">
                    {[1,2,3,4].map(i => (
                      <div
                        key={`credit-${i}`}
                        className={`h-1.5 sm:h-2 flex-1 rounded ${i <= driverState.R_credits_24 ? 'bg-emerald-500' : 'bg-slate-700'}`}
                      />
                    ))}
                  </div>
                </div>

                <div className="bg-slate-800/50 rounded-2xl border border-slate-700 p-4 sm:p-6">
                  <p className="text-slate-400 text-xs sm:text-sm">Time Until Rest</p>
                  <p className="text-xl sm:text-2xl font-bold text-white mt-1">{formatTime(driverState.time_until_rest_required_sec)}</p>
                  <p className="text-xs text-emerald-400 mt-2">
                    {driverState.need_rest_by_ts ? `Rest by ${new Date(driverState.need_rest_by_ts).toLocaleTimeString()}` : 'OK'}
                  </p>
                </div>
              </div>

              {/* Status Info */}
              <div className="bg-slate-800/50 rounded-2xl border border-slate-700 p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0">
                  <div>
                    <p className="text-slate-400 text-xs sm:text-sm">Current Mode</p>
                    <p className="text-base sm:text-lg font-semibold text-white">{driverState.diary_type}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-xs sm:text-sm">Work Stage</p>
                    <p className="text-base sm:text-lg font-semibold text-white">{driverState.current_work_stage}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-xs sm:text-sm">Provisional Mode</p>
                    <span className={`inline-block px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm ${
                      driverState.provisional_mode === 'FULL_ENFORCE' ? 'bg-emerald-500/20 text-emerald-400' :
                      driverState.provisional_mode === 'SOFT_ENFORCE' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-slate-700 text-slate-400'
                    }`}>
                      {driverState.provisional_mode}
                    </span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="bg-slate-800/50 rounded-2xl border border-slate-700 p-6 sm:p-8 text-center">
              <p className="text-slate-400 text-sm sm:text-base">No compliance data available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompliancePanel;
